'use client';

import { useEffect, useState } from 'react';
import { Button as AriaButton, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from 'react-aria-components';
import { useI18n } from '@/i18n';
import { useInstanceConfig } from '@/config/InstanceConfigProvider';
import { cx } from '@/utils/cx';

interface LanguageEntry {
    code: string;
    name: string;
    flag: string;
    bundled?: boolean;
    rtl?: boolean;
}

/** Tino Rangatiratanga (Māori sovereignty) flag */
function TinoRangatiratangaFlag({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 60 40" className={className} style={{ width: '1.25em', height: '0.85em' }}>
            <rect width="60" height="20" fill="#CC0000" />
            <rect y="20" width="60" height="20" fill="#000000" />
            <path d="M30 8 C22 8 16 14 16 20 C16 14 22 8 30 8 Z M30 32 C38 32 44 26 44 20 C44 26 38 32 30 32 Z" fill="#FFFFFF" />
            <circle cx="30" cy="20" r="8" fill="#FFFFFF" />
            <path d="M30 14 C26 14 24 17 24 20 C24 23 26 26 30 26 C27 26 25 23 25 20 C25 17 27 14 30 14 Z" fill="#CC0000" />
        </svg>
    );
}

function FlagDisplay({ entry }: { entry: LanguageEntry }) {
    if (entry.code === 'mi') return <TinoRangatiratangaFlag />;
    return <span className="text-base leading-none">{entry.flag}</span>;
}

// Bundled manifest — avoids a second network request on first load.
const BUNDLED_MANIFEST: LanguageEntry[] = [
    { code: 'en', name: 'English', flag: '🇬🇧', bundled: true },
    { code: 'mi', name: 'Te Reo', flag: '🇳🇿' },
];

interface LanguageSwitcherProps {
    className?: string;
    collapsed?: boolean;
    popoverPlacement?: 'top start' | 'right bottom';
}

export function LanguageSwitcher({ className, collapsed, popoverPlacement }: LanguageSwitcherProps) {
    const { lang, setLang, isLoading } = useI18n();
    const instanceConfig = useInstanceConfig();
    const [languages, setLanguages] = useState<LanguageEntry[]>(BUNDLED_MANIFEST);

    // Attempt to fetch latest manifest (may include admin-added languages).
    useEffect(() => {
        fetch('/locales/manifest.json')
            .then((r) => r.ok ? r.json() as Promise<{ languages: LanguageEntry[] }> : null)
            .then((data) => { if (data?.languages) setLanguages(data.languages); })
            .catch(() => { /* use bundled manifest */ });
    }, []);

    const current = languages.find((l) => l.code === lang) ?? languages[0];

    // NOTE-032: language selection is variant-gated. Funding ships English-only
    // (features.languageSelection = false), so hide the switcher entirely — no
    // flags, no Te Reo toggle in the nav. Re-enable later by flipping the flag in
    // the variant config (frontend/src/config/variants/funding.ts).
    if (!instanceConfig.features.languageSelection) {
        return null;
    }

    return (
        <div className={cx('relative', className)}>
            <AriaDialogTrigger>
                <AriaButton
                    className={cx(
                        'flex items-center rounded-md transition duration-100 ease-linear hover:bg-primary_hover focus:outline-none',
                        collapsed
                            ? 'size-10 justify-center'
                            : 'w-full gap-2 px-3 py-2 text-md text-secondary',
                    )}
                    aria-label="Select language"
                >
                    {current && <FlagDisplay entry={current} />}
                    {!collapsed && (
                        <>
                            <span className="font-medium">{current?.name}</span>
                            {isLoading && (
                                <svg className="ml-auto size-3.5 animate-spin text-fg-quaternary" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                                </svg>
                            )}
                            {!isLoading && (
                                <svg className="ml-auto size-4 text-fg-quaternary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M7 15l5 5 5-5" />
                                    <path d="M7 9l5-5 5 5" />
                                </svg>
                            )}
                        </>
                    )}
                </AriaButton>
                <AriaPopover
                    placement={popoverPlacement ?? (collapsed ? 'right bottom' : 'top start')}
                    offset={8}
                    crossOffset={collapsed ? 6 : 0}
                    className={({ isEntering, isExiting }) =>
                        cx(
                            'w-44 rounded-xl border border-secondary bg-primary shadow-lg will-change-transform',
                            isEntering &&
                                'duration-300 ease-out animate-in fade-in placement-right:slide-in-from-left-2 placement-top:slide-in-from-bottom-2 placement-bottom:slide-in-from-top-2',
                            isExiting &&
                                'duration-150 ease-in animate-out fade-out placement-right:slide-out-to-left-2 placement-top:slide-out-to-bottom-2 placement-bottom:slide-out-to-top-2',
                        )
                    }
                >
                    {languages.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => { setLang(l.code); }}
                            className={cx(
                                'flex w-full items-center gap-2.5 px-3 py-2 text-sm first:rounded-t-xl last:rounded-b-xl hover:bg-primary_hover',
                                l.code === lang ? 'font-semibold text-brand-700' : 'text-secondary',
                            )}
                        >
                            <FlagDisplay entry={l} />
                            <span>{l.name}</span>
                            {l.code === lang && (
                                <svg className="ml-auto size-3.5 text-brand-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                    ))}
                </AriaPopover>
            </AriaDialogTrigger>
        </div>
    );
}
